// VoiceSpectrum.Web.T2 · v10.0.18.6 · 2026-06-01 · VoiceSpectrum AI
//
// SetupMfaPage — standalone MFA enrollment for signed-in users.
//
// v10.0.18.6 Bug I: Fixed interface fields from snake_case to camelCase to match
//   the API's global JsonNamingPolicy.CamelCase (Program.cs:297). Also fixed the
//   verify-setup request body to send preEnrollToken (not pre_token).

import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Input } from '../components/ds';

// v10.0.18.6 Bug I: All fields camelCase to match API JsonNamingPolicy.CamelCase
interface EnrollResponse {
  otpauthUri: string;       // was otpauth_uri
  base32Secret: string;     // was base32_secret
  qrDataUrl: string;        // was qr_data_url
  preEnrollToken: string;   // was pre_token (NOTE: API field is PreEnrollToken, not PreToken)
}

export default function SetupMfaPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [enrollData, setEnrollData] = useState<EnrollResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.post<EnrollResponse>('/auth/mfa/enroll', {})
      .then(data => { setEnrollData(data); setLoading(false); })
      .catch(() => {
        setError('Could not start MFA enrollment. Please sign in again.');
        setLoading(false);
      });
  }, []);

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setMfaError('');
    if (totpCode.length !== 6) { setMfaError('Enter the 6-digit code.'); return; }
    setSubmitting(true);
    try {
      // v10.0.18.6 Bug I: was { code, pre_token: enrollData?.pre_token }
      // Fixed to send preEnrollToken (camelCase, matching API's VerifySetupRequest.PreEnrollToken)
      const result = await api.post<{ accessToken: string }>('/auth/mfa/verify-setup', {
        code: totpCode, preEnrollToken: enrollData?.preEnrollToken,
      });
      // v10.0.18.6 Bug I: was result.access_token — now result.accessToken
      login(result.accessToken);
      navigate('/dashboard', { replace: true });
    } catch {
      setMfaError('Invalid code. Please check your authenticator and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function shell(children: React.ReactNode) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-brand">
            <span className="brand-mark">VS</span>
            <span className="brand-text">VoiceSpectrum</span>
          </div>
          {children}
        </div>
      </div>
    );
  }

  if (loading) {
    return shell(<div style={{ textAlign: 'center', padding: 'var(--space-5) 0' }}><div className="spinner" /></div>);
  }

  if (error) {
    return shell(
      <>
        <h1 className="auth-title">Two-factor authentication</h1>
        <Alert variant="error">{error}</Alert>
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Button variant="secondary" block onClick={() => navigate('/login')}>Back to sign in</Button>
        </div>
      </>,
    );
  }

  return shell(
    <>
      <h1 className="auth-title">Set up two-factor authentication</h1>
      <p className="auth-subtitle">
        Scan the QR code with an authenticator app like Google Authenticator, Authy, or 1Password.
      </p>

      {/* v10.0.18.6 Bug I: was enrollData?.qr_data_url — now enrollData?.qrDataUrl */}
      {enrollData?.qrDataUrl && (
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-4)' }}>
          <img
            src={enrollData.qrDataUrl}
            alt="TOTP QR code"
            style={{ width: 180, height: 180, border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', padding: 8 }}
          />
        </div>
      )}

      {/* v10.0.18.6 Bug I: was enrollData?.base32_secret — now enrollData?.base32Secret */}
      {enrollData?.base32Secret && (
        <div style={{
          background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)',
          padding: '8px 10px', marginBottom: 'var(--space-4)',
          fontFamily: 'var(--font-mono)', fontSize: 12, wordBreak: 'break-all',
          color: 'var(--muted)', textAlign: 'center',
        }}>
          {enrollData.base32Secret}
          <div style={{ fontSize: 10.5, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Manual entry key
          </div>
        </div>
      )}

      {mfaError && <div style={{ marginBottom: 'var(--space-3)' }}><Alert variant="error">{mfaError}</Alert></div>}

      <form onSubmit={handleVerify} className="stack-3">
        <Input
          label="Verification code"
          type="text"
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          value={totpCode}
          onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          autoComplete="one-time-code"
          required
          autoFocus
          style={{ letterSpacing: '0.25em', textAlign: 'center', fontSize: 18 }}
        />
        <Button type="submit" block loading={submitting}>
          {submitting ? 'Verifying…' : 'Activate 2FA'}
        </Button>
      </form>
    </>,
  );
}
